/**
 * @author: lv.chen
 * @date: 2026/3/13
 * @desc: Bubble 组件演示文件，展示各种配置效果
 */
import React, { useState } from 'react';
import Bubble from './index';

import './demo.scss';

// 用于演示状态交互的组件
const DisabledDemo: React.FC = () => {
  const [disabled, setDisabled] = useState(false);

  return (
    <div className="dbi-component-bubble-demo__inline">
      <Bubble content="这个气泡不会显示" disabled={disabled}>
        <span className="demo-trigger">禁用状态：{disabled ? '是' : '否'}</span>
      </Bubble>
      <button className="demo-btn" onClick={() => setDisabled(!disabled)}>
        切换禁用状态
      </button>
    </div>
  );
};

/**
 * Demo 案例配置
 * 每个配置项对应文档页面中的一个演示场景
 */
export const demoConfig = [
  {
    title: '基础用法',
    description: '鼠标悬停显示气泡，默认配置：黑色主题、顶部显示、最大宽度500px',
    code: `<Bubble content="这是基础气泡内容">
  <span>悬停查看气泡</span>
</Bubble>`,
    element: (
      <Bubble content="这是基础气泡内容，展示默认配置效果">
        <span className="demo-trigger">悬停查看气泡</span>
      </Bubble>
    ),
  },
  {
    title: '完整配置',
    description: '展示组件所有可配置项，支持透传 tea-component Bubble 的其他属性',
    code: `<Bubble
  content="完整配置的气泡内容"
  placement="top"
  trigger="hover"
  maxWidth={500}
  dark
  arrowPointAtCenter
  closeOnScroll
  disabled={false}
  style={{ backgroundColor: '#1a1a2e' }}
  className="custom-bubble"
  openDelay={100}
  closeDelay={100}
>
  <span>完整配置</span>
</Bubble>`,
    element: (
      <Bubble
        content="完整配置的气泡内容，展示所有可配置项，支持透传底层属性"
        placement="top"
        trigger="hover"
        maxWidth={500}
        dark
        arrowPointAtCenter
        closeOnScroll
        disabled={false}
        style={{ backgroundColor: '#1a1a2e' }}
        className="custom-bubble"
        openDelay={100}
        closeDelay={100}
      >
        <span className="demo-trigger">完整配置</span>
      </Bubble>
    ),
  },
  {
    title: '位置配置 (placement)',
    description: '支持 top、bottom、left、right 四个方向',
    code: `<Bubble content="顶部气泡" placement="top">
  <span>Top</span>
</Bubble>
<Bubble content="底部气泡" placement="bottom">
  <span>Bottom</span>
</Bubble>
<Bubble content="左侧气泡" placement="left">
  <span>Left</span>
</Bubble>
<Bubble content="右侧气泡" placement="right">
  <span>Right</span>
</Bubble>`,
    element: (
      <div className="dbi-component-bubble-demo__inline">
        <Bubble content="顶部气泡 Top" placement="top">
          <span className="demo-trigger">Top</span>
        </Bubble>
        <Bubble content="底部气泡 Bottom" placement="bottom">
          <span className="demo-trigger">Bottom</span>
        </Bubble>
        <Bubble content="左侧气泡 Left" placement="left">
          <span className="demo-trigger">Left</span>
        </Bubble>
        <Bubble content="右侧气泡 Right" placement="right">
          <span className="demo-trigger">Right</span>
        </Bubble>
      </div>
    ),
  },
  {
    title: '触发方式 (trigger)',
    description: '支持 hover（悬停）、click（点击）、focus（聚焦）三种触发方式',
    code: `<Bubble content="悬停触发" trigger="hover">
  <span>Hover 触发</span>
</Bubble>
<Bubble content="点击触发" trigger="click">
  <span>Click 触发</span>
</Bubble>
<Bubble content="聚焦触发" trigger="focus">
  <input placeholder="Focus 触发" />
</Bubble>`,
    element: (
      <div className="dbi-component-bubble-demo__inline">
        <Bubble content="悬停触发" trigger="hover">
          <span className="demo-trigger">Hover 触发</span>
        </Bubble>
        <Bubble content="点击触发，点击其他区域关闭" trigger="click">
          <span className="demo-trigger">Click 触发</span>
        </Bubble>
        <Bubble content="聚焦触发" trigger="focus">
          <input className="demo-input" placeholder="Focus 触发" />
        </Bubble>
      </div>
    ),
  },
  {
    title: '自定义最大宽度 (maxWidth)',
    description: '默认最大宽度为 500px，可自定义设置',
    code: `<Bubble
  content="较长的气泡内容..."
  maxWidth={500}
>
  <span>默认 500px</span>
</Bubble>
<Bubble
  content="较长的气泡内容..."
  maxWidth={200}
>
  <span>自定义 200px</span>
</Bubble>`,
    element: (
      <div className="dbi-component-bubble-demo__inline">
        <Bubble
          content="这是一段较长的气泡内容，用于演示默认 500px 最大宽度的效果。当内容超过最大宽度时会自动换行显示。"
          maxWidth={500}
        >
          <span className="demo-trigger">默认 500px</span>
        </Bubble>
        <Bubble
          content="这是一段较长的气泡内容，用于演示自定义 200px 最大宽度的效果。当内容超过最大宽度时会自动换行显示。"
          maxWidth={200}
        >
          <span className="demo-trigger">自定义 200px</span>
        </Bubble>
      </div>
    ),
  },
  {
    title: '主题切换 (dark)',
    description: 'dark 属性控制是否使用黑色主题，默认为 true',
    code: `<Bubble content="黑色主题（默认）" dark>
  <span>黑色主题</span>
</Bubble>
<Bubble content="浅色主题" dark={false}>
  <span>浅色主题</span>
</Bubble>`,
    element: (
      <div className="dbi-component-bubble-demo__inline">
        <Bubble content="黑色主题（默认）" dark>
          <span className="demo-trigger">黑色主题</span>
        </Bubble>
        <Bubble content="浅色主题" dark={false}>
          <span className="demo-trigger">浅色主题</span>
        </Bubble>
      </div>
    ),
  },
  {
    title: '箭头居中 (arrowPointAtCenter)',
    description: 'arrowPointAtCenter 属性控制气泡箭头是否指向目标元素中心，默认为 true',
    code: `<Bubble content="箭头居中（默认）" arrowPointAtCenter>
  <span>箭头居中</span>
</Bubble>
<Bubble content="箭头不居中" arrowPointAtCenter={false}>
  <span>箭头不居中</span>
</Bubble>`,
    element: (
      <div className="dbi-component-bubble-demo__inline">
        <Bubble content="箭头居中（默认）" arrowPointAtCenter>
          <span className="demo-trigger">箭头居中</span>
        </Bubble>
        <Bubble content="箭头不居中" arrowPointAtCenter={false}>
          <span className="demo-trigger">箭头不居中</span>
        </Bubble>
      </div>
    ),
  },
  {
    title: '滚动关闭 (closeOnScroll)',
    description: 'closeOnScroll 属性控制页面滚动时是否自动关闭气泡，默认为 true',
    code: `<Bubble content="滚动关闭（默认）" closeOnScroll>
  <span>滚动关闭</span>
</Bubble>
<Bubble content="滚动不关闭" closeOnScroll={false}>
  <span>滚动不关闭</span>
</Bubble>`,
    element: (
      <div className="dbi-component-bubble-demo__inline">
        <Bubble content="滚动关闭（默认）" closeOnScroll>
          <span className="demo-trigger">滚动关闭</span>
        </Bubble>
        <Bubble content="滚动不关闭" closeOnScroll={false}>
          <span className="demo-trigger">滚动不关闭</span>
        </Bubble>
      </div>
    ),
  },
  {
    title: '禁用状态 (disabled)',
    description: '禁用后不显示气泡，只渲染子元素',
    code: `<Bubble content="这个气泡不会显示" disabled>
  <span>禁用状态</span>
</Bubble>`,
    element: <DisabledDemo />,
  },
  {
    title: '空内容处理',
    description: '当 content 为空时，直接渲染子元素，不显示气泡',
    code: `<Bubble content="">
  <span>content 为空，无气泡</span>
</Bubble>`,
    element: (
      <Bubble content="">
        <span className="demo-trigger">content 为空，无气泡</span>
      </Bubble>
    ),
  },
  {
    title: '长文本与英文单词完整性',
    description: '气泡内容支持长文本自动换行，英文单词保持完整性（不会从单词中间断开）',
    code: `<Bubble
  content="This is a long English sentence that demonstrates how words are kept intact..."
>
  <span>查看长文本效果</span>
</Bubble>`,
    element: (
      <Bubble content="This is a long English sentence that demonstrates how words are kept intact without breaking in the middle. 中文内容也会正常换行显示，不会出现文字截断的问题。这是一段很长的文字内容，用于测试气泡在各种情况下的显示效果。">
        <span className="demo-trigger">查看长文本效果</span>
      </Bubble>
    ),
  },
  {
    title: '自定义样式',
    description: '通过 style 和 className 自定义气泡样式',
    code: `<Bubble
  content="自定义样式：背景色、边框等"
  style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a4a6a' }}
>
  <span>自定义 style</span>
</Bubble>
<Bubble
  content="自定义类名样式"
  className="custom-bubble-class"
>
  <span>自定义 className</span>
</Bubble>`,
    element: (
      <div className="dbi-component-bubble-demo__inline">
        <Bubble
          content="自定义样式：背景色、边框等"
          style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a4a6a' }}
        >
          <span className="demo-trigger">自定义 style</span>
        </Bubble>
        <Bubble content="自定义类名样式" className="custom-bubble-class">
          <span className="demo-trigger">自定义 className</span>
        </Bubble>
      </div>
    ),
  },
];

export default demoConfig;
